import { canonicalTimeZone } from "@shared/time/canonicalZone";
import { timezoneCurrentTime, timezoneName } from "@/lib/timezones";

/**
 * The one way an instant reaches the screen (round 3, 2026-09-30, and the owner rule on #1078 and
 * #1019, 2026-10-15, which replaced round 3's "UTC is a hover away").
 *
 * The owner: "no user want to see the time in utc anywhere on an app like this. either it should
 * be in their preferred timezone from browser or default it to pst as we are based on portland".
 * The server stores and sends every instant in UTC; that is a storage format, never a display
 * format. Every instant the app renders goes through `formatInstant` in the person's display zone
 * (`useDisplayTz()`: the zone they chose, else this browser's), and the zone is named once per
 * screen or grid (`zoneCaption`) or beside a time that stands alone (`{ named: true }`). Every
 * hover over a shown instant is `instantHoverTitle`: the exact time in the same display zone with
 * the zone's full name. Pacific (`FALLBACK_ZONE`, America/Los_Angeles) stands in only where no
 * zone of the person is known, as when this engine will not report its own.
 * `client/src/__tests__/noUtcInstant.test.ts` refuses a raw ISO string or the word UTC for an
 * instant a person reads.
 *
 * What stays outside it: a calendar date with no time of day (a skip date, a credential's expiry
 * day) is a date, not an instant, and is formatted from its own fields; a schedule's own times
 * (its rule, its start and end) are in the zone the schedule was given (`lib/timezones.ts`), UTC
 * too when a person chose UTC for it, while its next run and its pause end, instants, are in the
 * display zone;
 * emails and Teams messages are the runner's (#1019: the recipient's zone, else Pacific).
 * Machine-readable exports for auditors keep ISO 8601 in UTC; a file a person saves from a screen
 * (the run's event log) is ISO 8601 with the display zone's offset (`shared/time/isoInZone.ts`).
 *
 * The locale is pinned to `en-US`, as `lib/timezones.ts` pins it: the product's clock times are
 * American, and a fixed locale is what makes the rendering testable.
 */

const LOCALE = "en-US";

/** What a missing or unreadable instant renders as: never "Invalid Date". */
export const NO_TIME = "—";

/**
 * - `seconds`: "Sep 30, 2026, 11:42:05 AM" (a run, an event, an audit row)
 * - `minutes`: "Sep 30, 2026, 11:42 AM" (most grids)
 * - `short`: "Sep 30, 11:42 AM" (a time in a sentence or a card)
 * - `time`: "11:42 AM" (a retry or a limit within the day)
 * - `day`: "Sep 30, 2026" (the display zone's day of an instant)
 */
export type InstantStyle = "seconds" | "minutes" | "short" | "time" | "day";

const STYLE_OPTIONS: Record<InstantStyle, Intl.DateTimeFormatOptions> = {
  seconds: {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  },
  minutes: { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" },
  short: { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" },
  time: { hour: "2-digit", minute: "2-digit" },
  day: { year: "numeric", month: "short", day: "2-digit" },
};

/** Where no zone of the person is known: Pacific, where Skypoint is (owner, #1078). */
export const FALLBACK_ZONE = "America/Los_Angeles";

const formatters = new Map<string, Intl.DateTimeFormat>();

/** A cached formatter; null when this engine cannot render the zone. */
function formatterFor(
  zone: string,
  key: string,
  options: Intl.DateTimeFormatOptions,
  locale = LOCALE,
): Intl.DateTimeFormat | null {
  const cacheKey = `${locale}|${zone}|${key}`;
  const cached = formatters.get(cacheKey);
  if (cached) return cached;
  try {
    const formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone });
    formatters.set(cacheKey, formatter);
    return formatter;
  } catch {
    return null;
  }
}

function parse(iso: string | Date | null | undefined): Date | null {
  if (!iso) return null;
  const date = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** True when this engine renders the zone. */
export function canRenderZone(zone: string | null | undefined): zone is string {
  return Boolean(zone) && formatterFor(zone as string, "probe", {}) !== null;
}

/**
 * A zone by its current IANA name ("Asia/Calcutta" is "Asia/Kolkata", `shared/time/
 * canonicalZone.ts`) when this engine renders that name, else as given: ICU lists and reports the
 * old names, and a person, the picker's search and the stored row all use the current one.
 */
export function currentZoneName(zone: string): string {
  const current = canonicalTimeZone(zone);
  return current !== zone && canRenderZone(current) ? current : zone;
}

/** This browser's zone, by its current name; Pacific when the engine will not say. */
export function browserTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return canRenderZone(zone) ? currentZoneName(zone) : FALLBACK_ZONE;
  } catch {
    return FALLBACK_ZONE;
  }
}

/** The wall clock of an instant in `zone`, read as if it were UTC (its milliseconds included). */
function wallClockAsUtc(at: Date, zone: string): number | null {
  const formatter = formatterFor(zone, "wall-clock", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  if (!formatter) return null;
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(formatter.formatToParts(at).find((p) => p.type === type)?.value ?? 0);
  return (
    Date.UTC(
      part("year"),
      part("month") - 1,
      part("day"),
      part("hour"),
      part("minute"),
      part("second"),
    ) + at.getUTCMilliseconds()
  );
}

/** The instant a wall clock (as UTC milliseconds) shows in `zone`, across a DST change too. */
function instantOfWallClock(wall: number, zone: string): Date {
  let at = wall;
  // Two passes settle the offset, the second for a day whose offset changes (DST).
  for (let pass = 0; pass < 2; pass += 1) {
    const shown = wallClockAsUtc(new Date(at), zone);
    if (shown === null) return new Date(wall);
    at += wall - shown;
  }
  return new Date(at);
}

/**
 * The first and last instants of a calendar day in `zone` (B-22, round 9): a date filter's
 * "from" day starts at that zone's midnight and its "to" day ends at 23:59:59.999 there, so the
 * rows a grid dates inside the range (in the same zone) are the rows the filter returns, whatever
 * the device's own zone. `month` is 1 to 12.
 */
export function dayBoundsInZone(
  year: number,
  month: number,
  day: number,
  zone: string,
): { start: Date; end: Date } {
  const shownZone = formatterFor(zone, "probe", {}) ? zone : FALLBACK_ZONE;
  const start = firstInstantOfDay(Date.UTC(year, month - 1, day), shownZone);
  const next = firstInstantOfDay(Date.UTC(year, month - 1, day + 1), shownZone);
  return { start, end: new Date(next.getTime() - 1) };
}

/**
 * The first instant whose wall clock in `zone` is on the day that starts at `midnight` (UTC
 * milliseconds of that wall clock). Where the clock jumps forward AT midnight (Santiago, Havana),
 * 00:00 does not exist: the settled instant shows the previous day's 23:00, and the day really
 * starts at the jump, as much later as the clock fell short.
 */
function firstInstantOfDay(midnight: number, zone: string): Date {
  const at = instantOfWallClock(midnight, zone);
  const shown = wallClockAsUtc(at, zone);
  return shown !== null && shown < midnight ? new Date(at.getTime() + (midnight - shown)) : at;
}

/** The calendar day an instant falls on in `zone`, as `{ year, month (1 to 12), day }`. */
export function calendarDayInZone(
  iso: string | Date,
  zone: string,
): { year: number; month: number; day: number } | null {
  const at = parse(iso);
  if (!at) return null;
  const wall = wallClockAsUtc(at, formatterFor(zone, "probe", {}) ? zone : FALLBACK_ZONE);
  if (wall === null) return null;
  const date = new Date(wall);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

/**
 * An instant in `zone`. `named` appends the zone's abbreviation ("Sep 30, 11:42 AM IST"), for a
 * time that is not under a caption. A zone this engine cannot render falls back to Pacific, named,
 * so the reader is never shown an unlabelled clock.
 */
export function formatInstant(
  iso: string | Date | null | undefined,
  zone: string,
  style: InstantStyle = "minutes",
  options: { named?: boolean } = {},
): string {
  const at = parse(iso);
  if (!at) return NO_TIME;
  let shownZone = zone;
  let formatter = formatterFor(zone, style, STYLE_OPTIONS[style]);
  if (!formatter) {
    shownZone = FALLBACK_ZONE;
    formatter = formatterFor(FALLBACK_ZONE, style, STYLE_OPTIONS[style]);
  }
  const text = formatter ? formatter.format(at) : NO_TIME;
  const named = options.named || shownZone !== zone;
  return named ? `${text} ${zoneAbbreviation(shownZone, at)}` : text;
}

/** The offset at the end of an instant, and what is left once it is taken off (kept apart so each pattern stays simple). */
const ISO_ZONE_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/;
const ISO_LOCAL_PART = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?$/;

function hasIsoShape(value: string): boolean {
  const zone = ISO_ZONE_SUFFIX.exec(value);
  return zone !== null && ISO_LOCAL_PART.test(value.slice(0, zone.index));
}

/**
 * True for a strict ISO-8601 timestamp with a time of day and an offset ("2026-10-23T23:21:37Z"),
 * the shape the server stores and sends an instant in (#1950). A day that does not exist, a calendar day, a bare number or a
 * text that merely holds digits is not one.
 */
export function isIsoInstant(value: unknown): value is string {
  if (typeof value !== "string" || !hasIsoShape(value)) return false;
  if (Number.isNaN(new Date(value).getTime())) return false;
  // A day that does not exist (Feb 30) is rolled over by Date: read the day's own fields.
  const [year, month, day] = value.slice(0, 10).split("-").map(Number) as [number, number, number];
  const calendar = new Date(Date.UTC(year, month - 1, day));
  return calendar.getUTCMonth() === month - 1 && calendar.getUTCDate() === day;
}

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const CALENDAR_MONTH = /^(\d{4})-(\d{2})$/;

/** A calendar date's own fields, at noon UTC so no zone moves it across a day boundary. */
function calendarDate(year: string, month: string, day: string): Date | null {
  const at = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));
  return at.getUTCMonth() === Number(month) - 1 && at.getUTCDate() === Number(day) ? at : null;
}

/**
 * A calendar DAY ("2026-09-30"), not an instant: the day it names, in the words every date on
 * screen uses, "Sep 30, 2026" (round 6 walk W25: the Integrity tab printed ISO days). It has no
 * zone, so it is formatted in UTC from its own fields and never moves. Anything else is shown as
 * it came.
 */
export function formatCalendarDay(value: string): string {
  const match = CALENDAR_DAY.exec(value);
  const at = match ? calendarDate(match[1]!, match[2]!, match[3]!) : null;
  const formatter = formatterFor("UTC", "calendar-day", STYLE_OPTIONS.day);
  return at && formatter ? formatter.format(at) : value;
}

/** A calendar MONTH ("2026-09"): "Sep 2026" (walk W25), the same way. */
export function formatCalendarMonth(value: string): string {
  const match = CALENDAR_MONTH.exec(value);
  const at = match ? calendarDate(match[1]!, match[2]!, "01") : null;
  const formatter = formatterFor("UTC", "calendar-month", { year: "numeric", month: "short" });
  return at && formatter ? formatter.format(at) : value;
}

/**
 * A chart's time axis, one label per point, in `zone` (lead addendum 2 and review r2,
 * 2026-09-30). `SkyChartBlock` draws every q-th category, q = ceil(56 px / slot), and a full
 * "Sep 23, 2026, 09:00 AM" is about three slots wide at the axis' 11 px, so the labels printed on
 * top of each other in the table drawer. Which labels are drawn depends on a point's position,
 * not on day boundaries: a freshness check's 720 hourly points drew one date and then bare times.
 * So when the points span more than one day every label is its day ("Sep 23"); when they fall on
 * one day the first label (index 0, always drawn) is the day and the rest are times ("09:00 PM").
 * Both are at most 8 characters: "08:30 AM", the widest, is 52.2 px in Chromium. The categories
 * are also the chart's hover and Table labels; the exact time of each observation must be
 * in a grid or list under the chart (the monitor drawer's grid, the run page's "When each load
 * finished").
 */
export function chartTimeLabels(isos: readonly string[], zone: string): string[] {
  const shownZone = canRenderZone(zone) ? zone : FALLBACK_ZONE;
  const dayKey = formatterFor(shownZone, "iso-day", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const dayLabel = formatterFor(shownZone, "axis-day", { month: "short", day: "2-digit" });
  const time = formatterFor(shownZone, "time", STYLE_OPTIONS.time);
  if (!dayKey || !dayLabel || !time) return isos.map(() => NO_TIME);
  const instants = isos.map(parse);
  const days = new Set(instants.flatMap((at) => (at ? [dayKey.format(at)] : [])));
  const oneDay = days.size <= 1;
  let named = false;
  return instants.map((at) => {
    if (!at) return NO_TIME;
    if (oneDay && named) return time.format(at);
    named = true;
    return dayLabel.format(at);
  });
}

const HOVER_OPTIONS: Intl.DateTimeFormatOptions = {
  ...STYLE_OPTIONS.seconds,
  timeZoneName: "long",
};

/**
 * The hover text of a shown instant (owner rule on #1078, 2026-10-15): the exact time, to the
 * second, in the reader's display zone with the zone's full name, "Oct 14, 2026, 12:42:00 PM
 * India Standard Time"; never UTC, never a raw ISO string. `zone` is `useDisplayTz()` (the saved
 * preference, else this browser's). A zone this engine cannot render reads in Pacific, named.
 * Undefined for a missing instant, so no empty tooltip is drawn. It is the one hover of every
 * instant the app shows (#1078).
 */
export function instantHoverTitle(
  iso: string | Date | null | undefined,
  zone: string,
): string | undefined {
  const at = parse(iso);
  if (!at) return undefined;
  const formatter =
    formatterFor(zone, "hover", HOVER_OPTIONS) ??
    formatterFor(FALLBACK_ZONE, "hover", HOVER_OPTIONS);
  return formatter ? formatter.format(at) : formatInstant(at, zone, "seconds", { named: true });
}

/**
 * How long ago a past instant was, "2 minutes ago", or "just now" under 45 seconds. A server
 * stamps the instant with its own clock and the browser reads it against this one, so an instant
 * a few seconds ahead (a browser clock behind the web's) is clamped to now: it never reads
 * "in 20 seconds" (#1017 review). Empty for a missing or unreadable instant. The absolute instant
 * goes in a `title` (`instantHoverTitle`).
 */
export function relativeAgo(iso: string | Date | null | undefined, now: Date = new Date()): string {
  const then = parse(iso);
  if (!then) return "";
  const seconds = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (seconds < 45) return "just now";
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["second", 60],
    ["minute", 60],
    ["hour", 24],
    ["day", 7],
    ["week", 4.35],
    ["month", 12],
  ];
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  let value = -seconds;
  for (const [unit, step] of units) {
    if (Math.abs(value) < step) return formatter.format(Math.round(value), unit);
    value /= step;
  }
  return formatter.format(Math.round(value), "year");
}

const UTC_NAMES = new Set(["UTC", "Etc/UTC", "Etc/UCT", "UCT", "GMT", "Etc/GMT", "Zulu"]);

/** True for a name of the UTC zone itself ("UTC", "Etc/UTC", "GMT" and the like). */
export function isUtcZone(zone: string): boolean {
  return UTC_NAMES.has(zone);
}

/**
 * Zones whose place name is not the city in their IANA id. Everything else is named by its city
 * ("Europe/London" is London); a US zone by the name the schedules use ("Eastern").
 */
const PLACE_NAMES: Readonly<Record<string, string>> = {
  // "Asia/Calcutta" reads "India" too: `placeName` asks by the current name.
  "Asia/Kolkata": "India",
};

/**
 * English locales asked, in turn, for a zone's abbreviation. `en-US` names only the US zones and
 * answers "GMT+5:30" for India; `en-IN` says IST, `en-GB` BST and CEST, `en-AU` AEST. When none
 * has a name, the offset stays: it is still true, and inventing a name would not be.
 */
const ABBREVIATION_LOCALES = ["en-US", "en-IN", "en-GB", "en-AU", "en-NZ", "en-SG", "en-ZA"];

function isOffset(name: string): boolean {
  return /^GMT[+-]/.test(name) || /^UTC[+-]/.test(name);
}

/** The abbreviation in force at `at`: IST, EDT, BST, AEST; else the offset, "GMT+9". */
export function zoneAbbreviation(zone: string, at: Date = new Date()): string {
  if (UTC_NAMES.has(zone)) return "UTC";
  let offset: string | null = null;
  for (const locale of ABBREVIATION_LOCALES) {
    const formatter = formatterFor(zone, "abbr", { timeZoneName: "short" }, locale);
    if (!formatter) return zone;
    const name = formatter.formatToParts(at).find((part) => part.type === "timeZoneName")?.value;
    if (!name) continue;
    if (!isOffset(name)) return name;
    offset ??= name;
  }
  return offset ?? zone;
}

/** What a person calls the zone: "India", "Eastern", "London", "UTC"; "Kyiv", never "Kiev". */
function placeName(given: string): string {
  if (UTC_NAMES.has(given)) return "UTC";
  const zone = canonicalTimeZone(given);
  const known = PLACE_NAMES[zone];
  if (known) return known;
  const scheduleName = timezoneName(zone);
  if (scheduleName !== zone) return scheduleName;
  if (zone.startsWith("Etc/")) return zone.slice(4);
  const city = zone.split("/").pop() ?? zone;
  return city.replace(/_/g, " ");
}

/** "India (IST)", "Eastern (EDT)", "Tokyo (GMT+9)", "UTC". */
export function zoneLabel(zone: string, at: Date = new Date()): string {
  const name = placeName(zone);
  const abbreviation = zoneAbbreviation(zone, at);
  if (!abbreviation || abbreviation === name) return name;
  if (name.endsWith(")")) return `${name.slice(0, -1)}, ${abbreviation})`;
  return `${name} (${abbreviation})`;
}

/** "Times in India (IST)": said once per screen or grid of instants. */
export function zoneCaption(zone: string, at: Date = new Date()): string {
  return `Times in ${zoneLabel(zone, at)}`;
}

/**
 * The IANA name under a picker option, by its current name: "Asia/Kolkata". A city the name
 * spells with underscores follows in words, "America/Los_Angeles (Los Angeles)": the picker's
 * search matches an option's text only (skypoint-ui `SkyDropdown`), and a person types
 * "Los Angeles", not "Los_Angeles". The region ("Asia") and the city ("Kolkata") are in the name.
 */
export function zoneIdLine(zone: string): string {
  const current = canonicalTimeZone(zone);
  const segment = current.split("/").pop() ?? current;
  const city = segment.replace(/_/g, " ");
  return city === segment ? current : `${current} (${city})`;
}

/** A picker option: "India (IST) · 11:42 AM", the way the schedule picker labels a zone. */
export function zoneOptionLabel(zone: string, at: Date = new Date()): string {
  const time = timezoneCurrentTime(zone, at);
  const label = zoneLabel(zone, at);
  return time ? `${label} · ${time}` : label;
}
